import { useEffect, useMemo, useRef, useState } from "react";
import { AssetPreviewDisplay } from "./AssetPreviewDisplay";
import { useTypedDispatch, useTypedSelector } from "../../state/hooks";
import Panel from "../layout/Panel";
import SearchField from "../layout/SearchField";
import { AssetTypes, LayoutOrientations, SortOrders } from "../../globals";
import Spacer from "../layout/Spacer";
import Separator from "../layout/Separator";
import { SortAscendingIcon, SortDescendingIcon, SolidCircleCloseIcon } from "../Iconset";
import SliderProperty from "../properties/SliderProperty";
import PropertyBase from "../properties/PropertyBase";
import Button from "../layout/Button";
import AssetsManager from "../../classes/AssetsManager";
import TextUtils from "../../classes/utilities/TextUtils";
import { StoreActions } from "../../state/store";
import { getUuidv4 } from "../../classes/utilities/Misc";
import Utils from "../../classes/Utilities";
import MaterialAsset from "../../classes/MaterialAsset";

export default function AssetPicker(props)
{
	const dispatch = useTypedDispatch();
	const library = useTypedSelector(state => state.assetModule.library);
	const projectAssets = useTypedSelector(state => state.assetModule.project);
	const materials = useTypedSelector(state => state.materialsModule.materials);
	const visageTemplates = useTypedSelector(state => state.visageModule.uiTemplateData);
	const selfRef = useRef(null);
	const guid = useRef(getUuidv4());
	const [searchQuery, setSearchQuery] = useState("");
	const [sortOrder, setSortOrder] = useState(SortOrders.Ascending);
	const scale = useTypedSelector(state => state.assetModule.imagePickerScale);
	const [selectedAssetID, setSelectedAssetID] = useState<string>(props.value || "");
	
	// Get the asset type(s) to display
	const validTypes = new Array<AssetTypes>().concat(props.type);
	
	useEffect(() => 
	{
	// Add ModelProcessed if Model is included
		if(validTypes.includes(AssetTypes.Model)) 
		{
			validTypes.push(AssetTypes.ModelProcessed);
		}
	}, [props.type]);
	
	// Get filter props
	const filter = props.filtered ?? new Set();
	const extensionFilter = props.extensionFilter ?? null;
	const showRevisions = props.showRevisions ?? false;
	const libraryOnly = props.libraryOnly ?? true;
	
	// Check if we're dealing with materials
	const isMaterialPicker = validTypes.includes(AssetTypes.Material);
	const isVisageTemplatePicker = validTypes.includes(AssetTypes.VisageTemplate);
	
	const assets = useMemo(() => 
	{
		const assetIds = new Set<string>();
		let items = new Array<string>();
		
		// Special handling for materials
		if (isMaterialPicker && materials)
		{
			// Add the default material option
			items.push(MaterialAsset.defaultMaterialGuid);
			
			// Add all materials from the materials module
			const materialItems = Object.keys(materials).filter(item => 
			{
				const materialInfo = materials[item];
				return materialInfo && 
					!filter.has(item) &&
					TextUtils.searchString(materialInfo.name.value, searchQuery);
			}).map(item => 
			{
				assetIds.add(item);
				return item;
			}).sort((a, b) => 
			{
				const sortMultiplier = sortOrder === SortOrders.Ascending ? 1 : -1;
				const aInfo = materials[a];
				const bInfo = materials[b];
				return aInfo.name.value.localeCompare(bInfo.name.value) * sortMultiplier;
			});
			
			items.push(...materialItems);
			return items;
		}

		if (isVisageTemplatePicker && visageTemplates)
		{
			const visageTemplateItems = Object.keys(visageTemplates).filter(item => 
			{
				const visageTemplateInfo = visageTemplates[item];
				return visageTemplateInfo && 
					!filter.has(item) && 
					TextUtils.searchString(visageTemplateInfo.name, searchQuery);
			}).map(item => 
			{
				assetIds.add(item);
				return item;
			}).sort((a, b) => 
			{
				const sortMultiplier = sortOrder === SortOrders.Ascending ? 1 : -1;
				const aInfo = visageTemplates[a];
				const bInfo = visageTemplates[b];
				return aInfo.name.localeCompare(bInfo.name) * sortMultiplier;
			});

			items.push(...visageTemplateItems);
			return items;
		}
		
		// Process library assets
		if (library)
		{
			for (const collectionKey of Object.keys(library))
			{
				const collection = library[collectionKey];
				if (collection)
				{
					const newAssets = Object.keys(collection).filter(item => 
					{
						const assetInfo = collection[item];
						return assetInfo && 
							validTypes.includes(assetInfo.type) && 
							!assetIds.has(item) && 
							!filter.has(item) &&
							(!extensionFilter || Utils.getFileExtension(assetInfo.name) === extensionFilter) &&
							TextUtils.searchString(assetInfo.name, searchQuery);
					}).map(item => 
					{
						assetIds.add(item);
						return item;
					}).sort((a, b) => 
					{
						const sortMultiplier = sortOrder === SortOrders.Ascending ? 1 : -1;
						const aProps = AssetsManager.getPropsForAsset(a);
						const bProps = AssetsManager.getPropsForAsset(b);
						return aProps!.label.localeCompare(bProps!.label) * sortMultiplier;
					});
					items.push(...newAssets);
					
					// Add revisions if needed
					if (showRevisions && validTypes.includes(AssetTypes.Model))
					{
						for (const item of newAssets)
						{
							const assetInfo = collection[item];
							if (assetInfo && assetInfo.revisions && assetInfo.revisions.length > 0)
							{
								const revisions = assetInfo.revisions
									.filter(revision => TextUtils.searchString(revision.name, searchQuery))
									.map(revision => revision.id)
									.sort((a, b) => 
									{
										const sortMultiplier = sortOrder === SortOrders.Ascending ? 1 : -1;
										const aInfo = assetInfo.revisions.find(r => r.id === a);
										const bInfo = assetInfo.revisions.find(r => r.id === b);
										return aInfo!.name.localeCompare(bInfo!.name) * sortMultiplier;
									});
								
								items.push(...revisions);
							}
						}
					}
				}
			}
		}
		
		// Process project assets if not libraryOnly
		if (!libraryOnly && projectAssets)
		{
			const uniqueProjectAssets = {};
			for (const key of Object.keys(projectAssets))
			{
				if (library && !(library.user[key] || library.organization[key]))
				{
					uniqueProjectAssets[key] = projectAssets[key];
				}
			}
			
			const projectItems = Object.keys(uniqueProjectAssets).filter(item => 
			{
				const assetProps = AssetsManager.getPropsForAsset(item);
				return assetProps && 
					validTypes.includes(assetProps.type) && 
					!assetIds.has(item) && 
					!filter.has(item) &&
					(!extensionFilter || Utils.getFileExtension(assetProps.label) === extensionFilter) &&
					TextUtils.searchString(assetProps.label, searchQuery);
			}).map(item => 
			{
				assetIds.add(item);
				return item;
			}).sort((a, b) => 
			{
				const sortMultiplier = sortOrder === SortOrders.Ascending ? 1 : -1;
				const aProps = AssetsManager.getPropsForAsset(a);
				const bProps = AssetsManager.getPropsForAsset(b);
				return aProps!.label.localeCompare(bProps!.label) * sortMultiplier;
			});
			
			items.push(...projectItems);
		}
		
		// Apply search filter if needed
		if (searchQuery !== "") 
		{
			items = items.filter(item => 
			{
				const name = AssetsManager.getPropsForAsset(item)?.label;
				if (!name) return false;
				return TextUtils.searchString(name, searchQuery);
			});
		}
		
		// Add "None" option at the beginning
		if (isMaterialPicker) 
		{
			// For materials, we already added the default material at the beginning
			return items;
		}
		else 
		{
			// For other asset types, add an empty option
			items.unshift("");
			return items;
		}
	}, [library, projectAssets, materials, searchQuery, sortOrder, validTypes, filter, extensionFilter, libraryOnly, isMaterialPicker]);

	function onAssetPicked()
	{
		if (props.assetSelectedCallback)
		{
			props.assetSelectedCallback(selectedAssetID);
		}

		if (props.onShutdown)
		{
			props.onShutdown();
		}
	}

	// Generate a label for the button based on the asset type
	const buttonLabel = isMaterialPicker 
		? "Select Material" 
		: isVisageTemplatePicker
			? "Select Visage Template"
			: validTypes.length === 1 && validTypes[0] === AssetTypes.Image 
				? "Select Image" 
				: "Select Asset";

	return (
		<Panel className="solid"
			data-name={props["data-name"]}
			style={{
				display: "flex",
				flexDirection: "column",
				height: "100%",
				maxHeight: "500px",
				maxWidth: "1000px",
				minWidth: "1000px",
				minHeight: "500px",
			}}
			ref={selfRef}
		>
			<div className={"solid assetBrowserWindowHeader centerContent"}>
				{
					sortOrder === SortOrders.Ascending ?
						<SortAscendingIcon onClick={(e: any) => { setSortOrder(SortOrders.Descending) }} />
						:
						<SortDescendingIcon onClick={(e: any) => { setSortOrder(SortOrders.Ascending) }} />
				}
				<Spacer size={{ x: "8px" }} />
				<SearchField
					id={guid.current}
					value={searchQuery}
					onChangeCallback={(v) => setSearchQuery(v)}
					autoFocus={true}
				/>
				<Spacer size={{ x: "8px" }} />
				<SolidCircleCloseIcon onClick={() => 
				{
					if (props.onShutdown) 
					{
						props.onShutdown();
					}
				}} />
			</div>
			<Separator orientation={LayoutOrientations.Horizontal} className={"dark"} />
			<Spacer size={{ y: "2px" }} />
			<AssetPreviewDisplay 
				noDrag={true} 
				assets={assets} 
				scale={scale} 
				listItems={scale < 2} 
				overrideDoubleClick={(assetId) => {setSelectedAssetID(assetId); onAssetPicked()}} 
				selectedAsset={selectedAssetID} 
				onClickCallback={(assetId) => {setSelectedAssetID(assetId ? assetId : "")}}
				selectable={true}
				noneOptionLabel={" - None - "}
				hideFolderNames={isMaterialPicker}
				evenSpace={true}
			/>
			<Separator orientation={LayoutOrientations.Horizontal} className={"dark"} />
			<Spacer size={{ y: "2px" }} />
			<PropertyBase style={{marginBottom: "0"}}>
				<Spacer size={{ x: "1px" }} />
				<div>{assets.length} {isMaterialPicker ? "materials" : isVisageTemplatePicker ? "templates" : validTypes.length === 1 && validTypes[0] === AssetTypes.Image ? "images" : "assets"}</div>
				<Separator orientation={LayoutOrientations.Vertical} className={"dark"} />
				<SliderProperty labelStyle={{width: "unset"}} label={"Scale: "} min={0.6} max={4.8} step={0.1} value={scale} hideField className={"sliderFieldCircleThumb"} style={{width: "100px"}} cellclass={"propertySmallCell"} onChangeCallback={(v) => dispatch(StoreActions.assets.setImagePickerScale(v))} />
				<Spacer size={{ x: "2px" }} />
			</PropertyBase>
			<Separator orientation={LayoutOrientations.Horizontal} className={"dark"} />
			<Button label={buttonLabel} style={{margin: "15px"}} onClick={onAssetPicked} data-name={"asset-picker-confirm"} />
		</Panel>
	)
}